import { Box, Checkbox, Flex, Icon, Text } from '@chakra-ui/react';
import { ChevronRight } from 'lucide-react';
import { ResponsiveCardRendererProps, ResponsiveCardSlot, ResponsiveResolvedField } from './types';

function groupBySlot<TData, TColId extends string>(
    fields: ResponsiveResolvedField<TData, TColId>[],
): Map<ResponsiveCardSlot | 'default', ResponsiveResolvedField<TData, TColId>[]> {
    const groups = new Map<ResponsiveCardSlot | 'default', ResponsiveResolvedField<TData, TColId>[]>();

    for (const field of fields) {
        const slot = field.slot ?? 'default';
        const group = groups.get(slot);
        if (group) {
            group.push(field);
        } else {
            groups.set(slot, [field]);
        }
    }
    return groups;
}

function FieldValue<TData, TColId extends string>({ field }: { field: ResponsiveResolvedField<TData, TColId> }) {
    const display = field.displayValue ?? field.rawValue;
    if (display === undefined || display === null) {
        return null;
    }
    return <>{display as React.ReactNode}</>;
}

function LabeledField<TData, TColId extends string>({ field }: { field: ResponsiveResolvedField<TData, TColId> }) {
    return (
        <Box minW={0}>
            <Text fontSize="xs" color="fg.muted" lineHeight="1.2">
                {field.headerName}
            </Text>
            <Text fontSize="sm" truncate>
                <FieldValue field={field} />
            </Text>
        </Box>
    );
}

function isStatusField<TData, TColId extends string>(field: ResponsiveResolvedField<TData, TColId>): boolean {
    const name = field.headerName.toLowerCase();
    const fieldName = field.field ? String(field.field).toLowerCase() : '';
    return name.includes('status') || fieldName.includes('status');
}

const TITLE_HINTS = ['name', 'title', 'patient', 'employee', 'description'];

/** Split camelCase / snake_case into lowercase words */
function splitWords(str: string): string[] {
    return str
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]/g, ' ')
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
}

function isActionColumn<TData, TColId extends string>(field: ResponsiveResolvedField<TData, TColId>): boolean {
    return !field.colDef.field && !!field.colDef.cellRenderer && typeof field.colDef.cellRenderer !== 'string';
}

function findTitleIndex<TData, TColId extends string>(remaining: ResponsiveResolvedField<TData, TColId>[]): number {
    // Pass 1: match on headerName words (strongest signal)
    const byHeader = remaining.findIndex((f) => {
        const words = splitWords(f.headerName);
        return TITLE_HINTS.some((hint) => words.includes(hint));
    });
    if (byHeader >= 0) {
        return byHeader;
    }

    // Pass 2: match on field name words
    const byField = remaining.findIndex((f) => {
        if (!f.field) {
            return false;
        }
        const words = splitWords(String(f.field));
        return TITLE_HINTS.some((hint) => words.includes(hint));
    });
    return byField;
}

function autoAssignFields<TData, TColId extends string>(fields: ResponsiveResolvedField<TData, TColId>[]) {
    let titleField: ResponsiveResolvedField<TData, TColId> | undefined;
    let statusField: ResponsiveResolvedField<TData, TColId> | undefined;
    const actionFields: ResponsiveResolvedField<TData, TColId>[] = [];
    const remaining: ResponsiveResolvedField<TData, TColId>[] = [];

    for (const f of fields) {
        if (isActionColumn(f)) {
            actionFields.push(f);
        } else if (!statusField && isStatusField(f)) {
            statusField = f;
        } else {
            remaining.push(f);
        }
    }

    // Find title: pinned left > name-like field > first field
    const pinnedIdx = remaining.findIndex((f) => f.colDef.pinned === 'left' || f.colDef.pinned === true);
    if (pinnedIdx >= 0) {
        titleField = remaining.splice(pinnedIdx, 1)[0];
    } else {
        const hintIdx = findTitleIndex(remaining);
        if (hintIdx >= 0) {
            titleField = remaining.splice(hintIdx, 1)[0];
        } else if (remaining.length > 0) {
            titleField = remaining.shift();
        }
    }

    return { titleField, statusField, actionFields, contentFields: remaining };
}

export function DefaultCardRenderer<TData, TColId extends string = string>({
    isSelected,
    isSelectable,
    selectionMode,
    visibleFields,
    onSelect,
}: ResponsiveCardRendererProps<TData, TColId>) {
    const groups = groupBySlot(visibleFields);

    const titleFields = groups.get('title') ?? [];
    const subtitleFields = groups.get('subtitle') ?? [];
    const headerFields = groups.get('header') ?? [];
    const metaFields = groups.get('meta') ?? [];
    const contentFields = groups.get('content') ?? [];
    const footerFields = groups.get('footer') ?? [];
    const actionsFields = groups.get('actions') ?? [];
    const defaultFields = groups.get('default') ?? [];

    const hasSlotAssignments =
        titleFields.length > 0 ||
        subtitleFields.length > 0 ||
        headerFields.length > 0 ||
        metaFields.length > 0 ||
        contentFields.length > 0 ||
        footerFields.length > 0 ||
        actionsFields.length > 0;

    let effectiveTitleFields = titleFields;
    const effectiveSubtitleFields = subtitleFields;
    const effectiveHeaderFields = headerFields;
    let effectiveMetaFields = metaFields;
    let effectiveContentFields = contentFields;
    const effectiveFooterFields = footerFields;
    let effectiveActionFields = actionsFields;

    if (!hasSlotAssignments && defaultFields.length > 0) {
        const auto = autoAssignFields(defaultFields);
        effectiveTitleFields = auto.titleField ? [auto.titleField] : [];
        effectiveMetaFields = auto.statusField ? [auto.statusField] : [];
        effectiveContentFields = auto.contentFields;
        effectiveActionFields = auto.actionFields;
    }

    const isMultiSelect = selectionMode === 'multiple';
    const isDisabled = !isSelectable;

    return (
        <Box
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            onClick={isDisabled ? undefined : onSelect}
            onKeyDown={
                isDisabled
                    ? undefined
                    : (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onSelect();
                          }
                      }
            }
            w="100%"
            textAlign="left"
            p="12px"
            borderWidth="1px"
            borderColor={isSelected ? 'purple.400' : { base: 'border', _dark: 'musa.dark.border' }}
            borderRadius="md"
            bg={isSelected ? { base: 'purple.50', _dark: 'purple.900/20' } : { base: 'white', _dark: 'musa.dark.500' }}
            _hover={isDisabled ? undefined : { bg: { base: 'gray.50', _dark: 'whiteAlpha.100' } }}
            cursor={isDisabled ? 'default' : 'pointer'}
            opacity={isDisabled ? 0.5 : 1}
            aria-disabled={isDisabled || undefined}
            transition="background 0.15s">
            <Flex alignItems="center" gap="8px">
                {isMultiSelect && (
                    <Box onClick={(e) => e.stopPropagation()}>
                        <Checkbox.Root
                            checked={isSelected}
                            onCheckedChange={isDisabled ? undefined : () => onSelect()}
                            disabled={isDisabled}
                            size="sm">
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                        </Checkbox.Root>
                    </Box>
                )}
                <Flex flexDirection="column" flex="1" gap="4px" minW={0}>
                    {/* Header */}
                    {effectiveHeaderFields.length > 0 && (
                        <Flex gap="8px" flexWrap="wrap">
                            {effectiveHeaderFields.map((f) => (
                                <Text key={f.colId} fontSize="xs" color="fg.muted">
                                    <FieldValue field={f} />
                                </Text>
                            ))}
                        </Flex>
                    )}

                    {/* Title + Meta */}
                    {effectiveTitleFields.length > 0 && (
                        <Flex gap="8px" alignItems="center">
                            {effectiveTitleFields.map((f) => (
                                <Text key={f.colId} fontWeight="600" fontSize="sm" truncate>
                                    <FieldValue field={f} />
                                </Text>
                            ))}

                            {effectiveMetaFields.length > 0 && (
                                <Flex gap="4px" flexShrink={0} alignItems="center" ml="auto">
                                    {effectiveMetaFields.map((f) => (
                                        <Box key={f.colId} fontSize="xs">
                                            <FieldValue field={f} />
                                        </Box>
                                    ))}
                                </Flex>
                            )}
                        </Flex>
                    )}

                    {/* Subtitle */}
                    {effectiveSubtitleFields.length > 0 && (
                        <Flex gap="8px">
                            {effectiveSubtitleFields.map((f) => (
                                <Text key={f.colId} fontSize="sm" color="fg.muted">
                                    <FieldValue field={f} />
                                </Text>
                            ))}
                        </Flex>
                    )}

                    {/* Content — 2-column grid */}
                    {effectiveContentFields.length > 0 && (
                        <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap="2px" columnGap="16px" pt="4px">
                            {effectiveContentFields.map((f) => (
                                <LabeledField key={f.colId} field={f} />
                            ))}
                        </Box>
                    )}

                    {/* Actions */}
                    {effectiveActionFields.length > 0 && (
                        <Flex gap="4px" pt="4px">
                            {effectiveActionFields.map((f) => (
                                <Box key={f.colId}>
                                    <FieldValue field={f} />
                                </Box>
                            ))}
                        </Flex>
                    )}

                    {/* Footer */}
                    {effectiveFooterFields.length > 0 && (
                        <Flex gap="8px" pt="4px" flexWrap="wrap">
                            {effectiveFooterFields.map((f) => (
                                <Text key={f.colId} fontSize="xs" color="fg.muted">
                                    <FieldValue field={f} />
                                </Text>
                            ))}
                        </Flex>
                    )}
                </Flex>

                <Icon color="fg.subtle" flexShrink={0}>
                    <ChevronRight />
                </Icon>
            </Flex>
        </Box>
    );
}
